import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { axiosInterceptor } from "app/services/http-services/request-interceptor";
import { useAuth } from "app/providers/AuthProvider";
import { useTranslation } from "react-i18next";
import queryKeys from "shared/services/queryKeys";
import { FormView, resolveDefaultFormViewId, resolveFormViewId } from "../services/formLayoutBuilder";

const FORM_VIEW_PREFERENCE_KEY = "form_layout.view";

export function useFormView(tableId?: string, urlViewId?: string | null, tableName?: string, enabled = true) {
    const { i18n } = useTranslation();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const queryKey = queryKeys.preferences.byUser(user?.id);
    const [selection, setSelection] = useState<{ tableId: string; viewId: string | null }>();
    const { data, isLoading: viewsLoading } = useQuery({
        queryKey: queryKeys.records.config("sys_form_views", { sys_table_id: tableId, active: true, language: i18n.language }),
        enabled: enabled && Boolean(tableId),
        staleTime: 60_000,
        retry: false,
        queryFn: async (): Promise<FormView[]> => (await axiosInterceptor.get<{ data: FormView[] }>(
            `/api/form_layout/tables/${tableId}/views`, { params: { language: i18n.language } },
        )).data.data,
    });
    const views = data ?? [];
    const { data: preferenceRecords = [], isLoading: preferenceLoading } = useQuery({
        queryKey,
        queryFn: async () => {
            const result = await new RecordManagement("sys_user_preference")
                .where("user_id", user?.id)
                .paginate(1, 5000)
                .run();
            return result.data;
        },
        enabled: enabled && Boolean(tableName && user?.id && views.length > 0),
        retry: false,
    });
    const preference = preferenceRecords.find((record) => record.table_name === tableName && record.preference_key === FORM_VIEW_PREFERENCE_KEY);
    let savedViewId: string | null = null;
    try {
        const value = typeof preference?.preference_value === "string"
            ? JSON.parse(preference.preference_value)
            : preference?.preference_value;
        savedViewId = typeof value?.view_id === "string" ? value.view_id : null;
    } catch {
        // A malformed preference must not prevent the form from opening.
    }
    const defaultViewId = resolveDefaultFormViewId(views);
    const viewId = selection && selection.tableId === tableId
        ? resolveFormViewId(views, selection.viewId, defaultViewId)
        : resolveFormViewId(views, urlViewId, savedViewId, defaultViewId);
    const { mutate } = useMutation({
        mutationFn: async (id: string | null) => {
            const response = await new RecordManagement("sys_user_preference")
                .where("table_name", tableName)
                .where("user_id", user?.id)
                .where("preference_key", FORM_VIEW_PREFERENCE_KEY)
                .first();
            if (!response || response.id === undefined) {
                await new RecordManagement("sys_user_preference").save({
                    user_id: user?.id,
                    table_name: tableName,
                    preference_key: FORM_VIEW_PREFERENCE_KEY,
                    preference_value: JSON.stringify({ view_id: id }),
                });
                return;
            }
            await new RecordManagement("sys_user_preference").update(response.id, {
                preference_value: JSON.stringify({ view_id: id }),
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });
    const selectView = (id: string | null) => {
        setSelection(tableId ? { tableId, viewId: id } : undefined);
        if (tableName && user?.id) mutate(id);
    };
    return { views, viewId, defaultViewId, selectView, isLoading: viewsLoading || preferenceLoading };
}
